import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ReactElement } from 'react';
import { Ban, Loader2, RotateCcw, Save, X } from 'lucide-react';

/** Ensure label + icon stay white on destructive (some themes override button text). */
const resetDestructiveButtonClass =
  '!text-white hover:!text-white [&_svg]:!text-white';

interface PolicySettingsActionsBaseProps {
  hasCustomizedSettings: boolean;
  policyName: string;
  isDirty: boolean;
  isSubmitting: boolean;
  readOnly?: boolean;
  /** When false, Save stays disabled (e.g. allow/block validation). Default true. */
  isValid?: boolean;
  onReset: () => void;
}

interface ResetOnlyPolicySettingsActionsProps extends PolicySettingsActionsBaseProps {
  /**
   * Allow/block auto-persist: no Save/Cancel — only show destructive reset when customized.
   */
  resetOnly: true;
  onSave?: never;
  onCancel?: never;
}

interface StandardPolicySettingsActionsProps extends PolicySettingsActionsBaseProps {
  resetOnly?: false;
  onSave: () => void;
  onCancel: () => void;
}

type PolicySettingsActionsProps =
  | ResetOnlyPolicySettingsActionsProps
  | StandardPolicySettingsActionsProps;

export function PolicySettingsActions(
  props: PolicySettingsActionsProps
): ReactElement | null {
  const {
    hasCustomizedSettings,
    policyName,
    isDirty,
    isSubmitting,
    readOnly = false,
    isValid = true,
    resetOnly = false,
    onReset,
  } = props;

  const renderResetAction = (): ReactElement | null => {
    if (!hasCustomizedSettings) {
      return null;
    }
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            size="sm"
            variant="destructive"
            type="button"
            disabled={isSubmitting}
            className={resetDestructiveButtonClass}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset to Defaults
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Policy Settings?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset all settings for &quot;{policyName}&quot; back to
              their default values. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onReset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Reset Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  // Hide all actions when in read-only mode
  if (readOnly) {
    return null;
  }

  if (resetOnly) {
    return (
      <div className="flex items-center justify-end pt-4 border-t">
        {renderResetAction()}
      </div>
    );
  }
  const { onSave, onCancel } = props;

  const saveDisabled = !isDirty || isSubmitting || !isValid;
  const blockedByInvalid = !isValid;

  const saveButton = (
    <Button
      size="sm"
      type="button"
      onClick={onSave}
      disabled={saveDisabled}
      aria-label={
        blockedByInvalid
          ? 'Save unavailable — fix invalid fields before saving'
          : 'Save'
      }
    >
      {isSubmitting ? (
        <Loader2 className="h-4 w-4 mr-1 shrink-0 animate-spin" aria-hidden />
      ) : blockedByInvalid ? (
        <Ban className="h-4 w-4 mr-1 shrink-0" aria-hidden />
      ) : (
        <Save className="h-4 w-4 mr-1 shrink-0" aria-hidden />
      )}
      {isSubmitting ? 'Saving...' : 'Save'}
    </Button>
  );

  return (
    <div className="flex items-center justify-between pt-4 border-t">
      <div className="space-x-2">
        {blockedByInvalid ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-not-allowed">
                {saveButton}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              Fix invalid fields before saving.
            </TooltipContent>
          </Tooltip>
        ) : (
          saveButton
        )}
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={onCancel}
          disabled={!isDirty || isSubmitting}
        >
          <X className="h-4 w-4 mr-1" />
          Cancel
        </Button>
      </div>

      {renderResetAction()}
    </div>
  );
}
